import { useEffect, useCallback, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { Interpreter, State } from 'xstate';
import { useDispatch } from 'react-redux';
import { inspect } from '@xstate/inspect';

import { useQuery } from 'react-query';
import { Form } from '~/api/models/leadads/form';

import { useStateBus } from '~/state/bridge';
import { getAgents } from '~/state/Agents/selectors';
import { getFacebookPages } from '~/api/routes/auth/user';

import machine, { LeadAdContext, LeadAdEvent, LeadAdSchema } from './machine';
import { getLeadAdForms } from '~/api/routes/leadad/forms';
import { TosContext, TosEvent, TosSchema } from './machine/tosMachine';
import { SubscriptionActor } from './machine/subscription';
import { useAuthSelector } from '~/theseus/containers/Auth';
import { getAdType } from '~/state/Campaign/selectors';
import { useAccountQuery, usePageQuery, usePagesQuery } from '~/theseus/hooks/queries';

interface LeadState {
  forms: Form[] | undefined;
  type: string;
  source: string;
  selectedForm: string;
  changeSource: (source: string) => void;
  changeType: (type: string) => void;
  needsTos: boolean;
  matches: State<LeadAdContext, LeadAdEvent, LeadAdSchema>['matches'];
  termsService: Interpreter<TosContext, TosSchema, TosEvent>;
  subscriptionRef: SubscriptionActor;
  toggleEnabled: () => void;
  openTosModal: () => void;
  toggleModes: () => void;
  selectCreated: (form: Form) => void;
  selectForm: (id: string) => void;
  resetSelected: () => void;
}

if (process.env.NODE_ENV === 'development') {
  inspect({
    iframe: false,
  });
}

function useLeadState(defaultSelectedForm?: string): LeadState {
  const [accountId] = useAccountQuery();
  const [pageId] = usePageQuery();
  const userId = useAuthSelector(state => state.context.userId);

  const { data: pages } = usePagesQuery();

  const pageInfo = useMemo(() => pages?.find(({ id }) => id === pageId), [pages, pageId]);

  const agents: string = useStateBus(getAgents);
  const rgId = useAuthSelector(state => state.context.rgUser?.rg_info?.id);
  const adType = useStateBus(getAdType);
  const dispatch = useDispatch();

  const creatorId = useMemo(() => {
    const agent = agents.find(e => e.locutus_id === rgId);

    if (agent) {
      return agent.id;
    }

    const potentialAgent = agents.find(e => e.owner === true);

    if (potentialAgent) {
      return potentialAgent.id;
    }

    return null;
  }, [agents, rgId]);

  const [state, send] = useMachine(() => machine(dispatch), {
    devTools: process.env.NODE_ENV !== 'production',
    context: {
      type: 'Buyer',
      source: 'RG FB Tool Lead Ad',
      selectedForm: defaultSelectedForm || null,
    },
  });

  useEffect(() => {
    if (userId) {
      send({ type: 'USER_ID.change', id: userId });
    }
  }, [send, userId]);

  useEffect(() => {
    if (accountId) {
      send({ type: 'ACCOUNT_ID.change', id: accountId });
    }
  }, [send, accountId]);

  useEffect(() => {
    send({ type: 'CREATOR_ID.change', id: creatorId });
  }, [send, creatorId]);

  useEffect(() => {
    if (pageInfo) {
      send({ type: 'PAGE_INFO.change', pageInfo });
    }
  }, [send, pageInfo]);

  useEffect(() => {
    if (adType === 'boosted' || adType === 'post_only') {
      send('HIDE');
    } else {
      send('SHOW');
    }
  }, [send, adType]);

  const { data: forms } = useQuery(
    ['leadad', 'forms', pageInfo?.id],
    () => getLeadAdForms(pageInfo?.id),
    {
      initialData: [] as Form[],
      enabled: state.matches('enabled.loadingFormData') && !!pageInfo?.id,
      onSuccess: (_forms) => {
        if (_forms.length === 0) {
          send('FORMS.noForms');
        } else {
          send('FORMS.retrieved');
        }
      },
      onError: () => {
        // TODO: Toast here
        send('FORMS.noForms');
      },
    },
  );

  const openTosModal = useCallback(() => {
    send('MODAL.open');
  }, [send]);

  const toggleEnabled = useCallback(() => {
    send('TOGGLE_ENABLED');
  }, [send]);

  const toggleModes = useCallback(() => {
    send('FORM_MANAGER.toggleModes');
  }, [send]);

  const selectForm = useCallback(
    (id: string) => {
      send({ type: 'FORMS.select', id });
    },
    [send],
  );

  const selectCreated = useCallback(
    (form: Form) => {
      send({ type: 'FORMS.created', form });
    },
    [send],
  );

  const resetSelected = useCallback(() => {
    send({ type: 'FORMS.reset' });
  }, [send]);

  const changeType = useCallback((leadType: string) => {
    send({ type: 'TYPES.change', leadType });
  }, [send]);

  const changeSource = useCallback((source: string) => {
    send({ type: 'SOURCES.change', source });
  }, [send]);

  return {
    forms,
    type: state.context.type,
    source: state.context.source,
    changeType,
    changeSource,
    selectedForm: state.context.selectedForm,
    subscriptionRef: state.children['lead-ad-subscriptions'],
    matches: useCallback((stateVal: unknown) => state.matches(stateVal), [state.matches]),
    termsService: state.children['lead-ad-tos'],
    needsTos: state.matches('disabled.needsTos'),
    toggleEnabled,
    openTosModal,
    selectForm,
    selectCreated,
    toggleModes,
    resetSelected,
  };
}

export default useLeadState;
